<template>
  <AlertDialog :open="Boolean(dialog)" @update:open="DialogService.close(false)">
    <AlertDialogContent
      v-if="dialog"
      class="tw:flex tw:flex-col tw:max-h-[calc(100dvh-var(--app-safe-top)-var(--app-safe-bottom)-2rem)] tw:w-[calc(100%-var(--dialog-inline-gap))] tw:max-w-sm tw:rounded-(--radius-dialog) tw:bg-card tw:p-5"
    >
      <Button
        variant="close"
        size="icon-close"
        class="tw:absolute tw:top-3.5 tw:right-3.5"
        :aria-label="t('nkf.close')"
        @click="DialogService.close(false)"
        ><AppIcon size="action" name="x"
      /></Button>
      <AlertDialogHeader class="tw:flex-none tw:items-center tw:pt-6"
        ><span
          v-if="dialog.icon"
          class="confirm-icon tw:grid tw:place-items-center tw:bg-(--warning-soft) tw:text-(--warning) tw:w-14.5 tw:h-14.5 tw:mb-1.5 tw:rounded-(--radius-note)"
          ><AppIcon size="dialog" :name="dialog.icon" /></span
        ><AlertDialogTitle class="tw:type-confirm tw:tracking-normal">{{
          dialog.title
        }}</AlertDialogTitle
        ><AlertDialogDescription class="tw:type-dialog-body tw:text-center">{{
          dialog.description
        }}</AlertDialogDescription></AlertDialogHeader
      >
      <div v-if="dialog.details?.length" class="tw:min-h-0 tw:overflow-y-auto tw:space-y-3">
        <section
          v-for="group in dialog.details"
          :key="group.title"
          class="tw:rounded-(--radius-note) tw:bg-muted/40 tw:p-3"
        >
          <h3 class="tw:type-secondary-strong tw:mb-2">{{ group.title }}</h3>
          <dl class="tw:space-y-2">
            <div
              v-for="row in group.rows"
              :key="row.label"
              class="tw:flex tw:justify-between tw:gap-3 tw:type-body-small"
            >
              <dt class="tw:text-muted-foreground">{{ row.label }}</dt>
              <dd class="tw:min-w-0 tw:text-right tw:wrap-anywhere">
                <template v-if="row.previousValue !== undefined && row.previousValue !== row.value">
                  <span class="tw:sr-only">{{ t('nkf.confirmation.currentValue') }}: </span>
                  <span class="tw:text-muted-foreground">{{ row.previousValue }}</span>
                  <span aria-hidden="true"> → </span>
                  <span class="tw:sr-only">{{ t('nkf.confirmation.newValue') }}: </span>
                </template>
                <b>{{ row.value }}</b>
                <small v-if="row.source" class="tw:block tw:type-meta tw:text-muted-foreground"
                  ><template v-if="row.previousSource && row.previousSource !== row.source"
                    >{{ row.previousSource }} → </template
                  >{{ row.source }}</small
                >
                <template v-if="row.previousDateTime && row.previousDateTime !== row.dateTime">
                  <small class="tw:block tw:type-meta tw:text-muted-foreground">{{
                    t('nkf.confirmation.currentValue')
                  }}</small>
                  <AppHistoryDate :value="row.previousDateTime" show-time />
                  <small class="tw:block tw:type-meta tw:text-muted-foreground">{{
                    t('nkf.confirmation.newValue')
                  }}</small>
                </template>
                <AppHistoryDate v-if="row.dateTime" :value="row.dateTime" show-time />
              </dd>
            </div>
          </dl>
        </section>
      </div>
      <p v-if="dialog.restriction" role="status" class="tw:type-body-small tw:text-destructive">
        {{ dialog.restriction }}
      </p>
      <AppDialogActions class="tw:flex-none">
        <template #secondary>
          <Button variant="outline" @click="DialogService.close(false)">{{
            dialog.cancelLabel || t('nkf.cancel')
          }}</Button>
        </template>
        <Button
          :variant="dialog.confirmVariant || (dialog.destructive ? 'destructive' : 'default')"
          :disabled="dialog.confirmDisabled"
          @click="DialogService.close(true)"
          >{{ dialog.confirmLabel || t('nkf.confirm') }}</Button
        >
      </AppDialogActions>
    </AlertDialogContent>
  </AlertDialog>
</template>
<script setup>
// 1. Import necessary components
import { onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import AppIcon from './AppIcon.vue';
import AppHistoryDate from './AppHistoryDate.vue';
import AppDialogActions from './AppDialogActions.vue';
import { DialogService } from '@/shared/utilities/feedback/dialog-service';
// 2. Use
const { t } = useI18n();
const dialog = DialogService.active;
// 3. Define a reactive variable
// 4. Define methods or computed properties if needed
// 5. VueJS Hooks
onBeforeUnmount(() => DialogService.close(false));
</script>
